import { isPaymentAccount } from '@outss/ledger-core';
import { featureOfKind } from '@outss/ledger-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { COMING_SOON } from '@/app/navigation';
import { AmountInput } from '@/components/amount-input';
import { Combobox, type ComboItem } from '@/components/combobox';
import { ExchangeRateField, HomeTotals, useEffectiveRate } from '@/components/currency';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { OverlayPage } from '@/components/overlay-page';
import { ApplyDebitNoteDialog } from '@/components/purchases/apply-debit-note-dialog';
import { ApprovalStrip } from '@/components/portal/approval-strip';
import { AttachmentsPanel } from '@/components/portal/attachments-panel';
import { InboxSource } from '@/components/portal/inbox-source';
import { StatusBadge } from '@/components/sales/status-badge';
import { VoidDialog } from '@/components/sales/void-dialog';
import {
  ClassSelect,
  ProjectSelect,
  TrackingHeaderFields,
} from '@/components/tracking/tracking-fields';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { formatRate, taxCodesFor, useAccounts, useTaxCodes, type TaxCode } from '@/lib/accounts';
import { ApiError, api } from '@/lib/api';
import { formatTrn, useMe } from '@/lib/clients';
import { formatAddress, useContacts, type Contact } from '@/lib/contacts';
import { exponentOf, fromHomePrice, toHomeMinor, useHomeCurrency } from '@/lib/currencies';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { useItems } from '@/lib/items';
import { formatMinor, minorToPlain, toMinor } from '@/lib/money';
import { openNewDrawer } from '@/lib/new-drawer';
import { useOrgId } from '@/lib/org';
import {
  EMPTY_TRACKING,
  trackingBody,
  trackingFrom,
  useTrackingSettings,
  type TrackingValue,
} from '@/lib/tracking';
import { setDocumentTemplateSeed } from '@/lib/recurring-documents';
import {
  clientCanEdit,
  formReadOnly,
  useDocumentApprovals,
  useInboxItem,
  useOrgRole,
  postingOf,
  useFeatureSet,
  type InboxItem,
} from '@/lib/portal';
import {
  PURCHASE_INFO,
  canLimitRecovery,
  isSelfAssessed,
  purchaseLineAmounts,
  recoverableTax,
  usePurchaseDocuments,
  type PurchaseDocument,
  type PurchaseKind,
} from '@/lib/purchases';
import { PAYMENT_METHODS, rateFor, type AmountsAre } from '@/lib/sales';
import { TERMS, useCompanySettings } from '@/lib/settings';
import { cn } from '@/lib/utils';
import { isIndia, taxWord } from '@/lib/org-locale';

type Grid = 'category' | 'item';

interface Row {
  key: number;
  grid: Grid;
  itemId: string | null;
  accountId: string | null;
  description: string;
  quantity: string;
  /** Category lines: the amount; item lines: the rate. */
  rate: string;
  taxCodeId: string | null;
  /** Recoverable share of the vendor's VAT, percent ("100", "0", "60"). */
  recoverable: string;
  /** Class (when tracked per row) and the customer project the cost belongs to. */
  classId: string | null;
  projectId: string | null;
  /** Bills: the purchase order line this row receives, and the order's number. */
  poLineId: string | null;
  poNumber: string | null;
  /** Purchase orders: quantity received on bills so far. */
  received: string | null;
}

let seq = 0;
const emptyRow = (grid: Grid): Row => ({
  key: ++seq,
  grid,
  itemId: null,
  accountId: null,
  description: '',
  quantity: '',
  rate: '',
  taxCodeId: null,
  recoverable: '100',
  classId: null,
  projectId: null,
  poLineId: null,
  poNumber: null,
  received: null,
});
const used = (r: Row) =>
  Boolean(r.itemId || r.accountId || r.description.trim() || r.rate) &&
  (r.grid === 'category' ? Boolean(r.rate || r.accountId) : true);

const POSTED = ['approved', 'partially_paid', 'paid'];
/** Detail types whose input VAT is blocked by default (Executive Regulation art. 53). */
const BLOCKED_BY_DEFAULT = new Set(['entertainment']);

interface FormState {
  contactId: string | null;
  termsDays: number | null;
  issueDate: string;
  dueDate: string;
  dueEdited: boolean;
  supplyDate: string;
  reference: string;
  amountsAre: AmountsAre;
  memo: string;
  creditReason: string;
  relatedDocumentId: string | null;
  paymentAccountId: string | null;
  paymentMethod: string;
  /** Exchange rate typed on a foreign-currency document; '' = the day's rate from Currencies. */
  fxRate: string;
  /** Location, class and tags (document 06 L4); projects sit on the lines. */
  tracking: TrackingValue;
  rows: Row[];
}

const addDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

/** The open part of each purchase order line, as bill rows receiving it (Copy to bill). */
function openPurchaseOrderRows(po: PurchaseDocument): Row[] {
  return po.lines.flatMap((l) => {
    const left = Number(l.quantity) - Number(l.receivedQuantity ?? 0);
    if (l.closed || left <= 0) return [];
    const qty = String(Math.round(left * 1e6) / 1e6);
    return [
      {
        key: ++seq,
        grid: l.itemId ? 'item' : 'category',
        itemId: l.itemId,
        accountId: l.accountId,
        description: l.description ?? '',
        quantity: l.itemId ? qty : '',
        // category rows are received whole (quantity 1), item rows by the quantity left
        rate: l.unitPrice,
        taxCodeId: l.taxCodeId,
        recoverable: '100',
        classId: l.classId ?? null,
        projectId: l.projectId ?? null,
        poLineId: l.lineId ?? null,
        poNumber: po.number,
        received: null,
      } satisfies Row,
    ];
  });
}

function initialState(
  doc: PurchaseDocument | null,
  source: PurchaseDocument | null,
  contactId: string | null,
  receive: PurchaseDocument | null = null,
): FormState {
  const from = doc ?? source;
  const rows: Row[] = from
    ? from.lines.map((l) => ({
        key: ++seq,
        grid: l.itemId ? 'item' : 'category',
        itemId: l.itemId,
        accountId: l.accountId,
        description: l.description ?? '',
        quantity: l.itemId ? l.quantity : '',
        rate: l.itemId ? l.unitPrice : l.unitPrice,
        taxCodeId: l.taxCodeId,
        recoverable: String(Number(l.recoverablePercent)),
        classId: l.classId ?? null,
        projectId: l.projectId ?? null,
        // a copy is a new document: it receives nothing
        poLineId: doc ? (l.purchaseOrderLineId ?? null) : null,
        poNumber: doc ? (l.purchaseOrderNumber ?? null) : null,
        received: doc ? (l.receivedQuantity ?? null) : null,
      }))
    : receive
      ? openPurchaseOrderRows(receive)
      : [];
  while (rows.filter((r) => r.grid === 'category').length < 2) rows.push(emptyRow('category'));
  while (rows.filter((r) => r.grid === 'item').length < 2) rows.push(emptyRow('item'));
  return {
    contactId: from?.contactId ?? receive?.contactId ?? contactId,
    termsDays: doc?.termsDays ?? source?.termsDays ?? null,
    issueDate: doc?.issueDate ?? todayIso(),
    dueDate: doc?.dueDate ?? '',
    dueEdited: Boolean(doc?.dueDate),
    supplyDate: doc?.supplyDate ?? '',
    reference: doc?.reference ?? '',
    amountsAre: from?.amountsAre ?? receive?.amountsAre ?? 'exclusive',
    memo: from?.memo ?? '',
    creditReason: doc?.creditReason ?? '',
    relatedDocumentId: doc?.relatedDocumentId ?? null,
    paymentAccountId: from?.paymentAccountId ?? null,
    paymentMethod: from?.paymentMethod ?? '',
    fxRate: doc && doc.currency !== doc.homeCurrency ? doc.fxRate : '',
    tracking: doc
      ? trackingFrom(doc)
      : source
        ? { ...trackingFrom(source), tagIds: [] }
        : receive
          ? { ...trackingFrom(receive), projectId: null, tagIds: [] }
          : EMPTY_TRACKING,
    rows,
  };
}

/**
 * Bill, expense and debit note form (document 05 pattern 3; document 06 E3, E4, E6): Category
 * details and Item details grids, VAT per line with the recoverable share, reverse charge and
 * import VAT self-assessed. New and draft documents save as drafts or approve (number and post);
 * approved ones save as a repost; void ones are read-only.
 */
export function PurchaseForm({
  kind,
  doc,
  source,
  defaultContactId,
  openApply,
  fromInbox,
  receive,
}: {
  kind: PurchaseKind;
  doc: PurchaseDocument | null;
  source?: PurchaseDocument | null;
  defaultContactId?: string;
  openApply?: boolean;
  /** Document inbox upload this bill or expense is created from. */
  fromInbox?: string;
  /** New bill: the purchase order it receives (Copy to bill, document 06 E11). */
  receive?: PurchaseDocument;
}) {
  const info = PURCHASE_INFO[kind];
  const isPO = kind === 'purchase_order';
  const orgId = useOrgId()!;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const settings = useCompanySettings();
  // an expense may pay any contact, or no one (QBO's optional payee)
  const contacts = useContacts(kind === 'expense' ? {} : { kind: 'vendor' });
  const items = useItems();
  const taxCodes = useTaxCodes({ includeInactive: true });
  const accounts = useAccounts();
  const homeFallback = useHomeCurrency();
  const isStaff = me.data?.isStaff ?? false;
  // client portal: a client owner drafts bills for Outss to approve, where switched on
  const role = useOrgRole();
  const features = useFeatureSet();
  const docApprovals = useDocumentApprovals(doc?.status === 'draft' ? doc.id : undefined);
  const posting = postingOf(features, featureOfKind(kind));
  const clientEdit =
    !isStaff &&
    clientCanEdit({
      role,
      posting,
      kind,
      doc,
      approvals: docApprovals.data,
    });
  // under approval posting the owner's save is a draft for the accountant; under direct posting
  // (ADR-0017) the owner saves and posts like staff
  const clientDraft = clientEdit && posting === 'approval';
  const inbox = useInboxItem(!doc && fromInbox ? fromInbox : undefined);
  const vendorWord = kind === 'expense' ? 'Payee' : 'Vendor';

  const [s, setS] = useState<FormState>(() =>
    initialState(doc, source ?? null, defaultContactId ?? null, receive ?? null),
  );
  const initial = useRef(JSON.stringify(s));
  const [errors, setErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [voiding, setVoiding] = useState(false);
  const [applying, setApplying] = useState(Boolean(openApply));
  const [showItems, setShowItems] = useState(
    () =>
      Boolean(doc?.lines.some((l) => l.itemId)) || (settings.data?.expenses.showItemsTable ?? true),
  );
  const leaving = useRef(false);

  const isVoid = doc?.status === 'void';
  const posted = Boolean(doc && POSTED.includes(doc.status));
  const poReceived = isPO && Boolean(doc?.lines.some((l) => Number(l.receivedQuantity ?? 0) > 0));
  // every line received in full: the order closes itself and cannot be reopened (rule PO4)
  const poFullyReceived =
    isPO &&
    Boolean(doc?.lines.length) &&
    Boolean(doc?.lines.every((l) => Number(l.receivedQuantity ?? 0) >= Number(l.quantity)));
  // a received purchase order keeps its lines: close it, or edit the bills (migration 1711)
  const readOnly = formReadOnly({ isStaff, clientEdit, doc: doc ?? null }) || poReceived;
  const dirty = JSON.stringify(s) !== initial.current;

  useBlocker({
    shouldBlockFn: () =>
      dirty &&
      !leaving.current &&
      !window.confirm('You have unsaved changes. Leave without saving?'),
    enableBeforeUnload: () => dirty && !leaving.current,
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setS((p) => ({ ...p, [k]: v }));
  const tracking = useTrackingSettings();
  // QBO: Class and Customer/project columns on both grids, when in use
  const showReceived = isPO && Boolean(doc);
  const showPO = kind === 'bill' && s.rows.some((r) => r.poNumber);
  const trackingHeads = (
    <>
      {showReceived ? <th className="w-[90px] px-2 py-2 text-right">Received</th> : null}
      {showPO ? <th className="w-[110px] px-2 py-2">Purchase order</th> : null}
      {tracking.classes === 'per_line' ? <th className="w-[140px] px-2 py-2">Class</th> : null}
      {tracking.projects ? <th className="w-[160px] px-2 py-2">Project</th> : null}
    </>
  );
  const trackingCells = (r: Row, n: string) => (
    <>
      {showReceived ? (
        <td className="tnum px-2 py-1.5 text-right text-muted-foreground">{r.received ?? ''}</td>
      ) : null}
      {showPO ? (
        <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.poNumber ?? ''}</td>
      ) : null}
      {tracking.classes === 'per_line' ? (
        <td className="px-1 py-1.5">
          <ClassSelect
            ariaLabel={`Class, ${n}`}
            disabled={readOnly}
            value={r.classId}
            onChange={(classId) => updateRow(r.key, { classId })}
          />
        </td>
      ) : null}
      {tracking.projects ? (
        <td className="px-1 py-1.5">
          <ProjectSelect
            ariaLabel={`Project, ${n}`}
            disabled={readOnly}
            value={r.projectId}
            onChange={(projectId) => updateRow(r.key, { projectId })}
          />
        </td>
      ) : null}
    </>
  );
  const contact: Contact | undefined = contacts.data?.find((c) => c.id === s.contactId);
  // multi-currency: a document is in its vendor's currency (document 06 E3)
  const home = doc?.homeCurrency ?? homeFallback;
  const currency = contact?.currency ?? doc?.currency ?? home;
  const foreign = currency !== home;
  const exp = exponentOf(currency);
  const fxRate = useEffectiveRate(currency, home, s.issueDate, s.fxRate, doc?.fxRate);
  const toHome = (m: bigint) => (fxRate ? toHomeMinor(m, fxRate, exp, exponentOf(home)) : null);
  const purchaseCodes = useMemo(() => taxCodesFor(taxCodes.data, 'purchases'), [taxCodes.data]);
  const codeById = useMemo(
    () => new Map((taxCodes.data ?? []).map((t) => [t.id, t])),
    [taxCodes.data],
  );
  const accountById = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  );
  const defaultCode = (c?: Contact) =>
    c?.defaultTaxCodeId ??
    purchaseCodes.find((t) => t.code === 'SR')?.id ??
    purchaseCodes[0]?.id ??
    null;
  const vatRegistered = settings.data?.vat.vatRegistered ?? true;

  // ---- totals (bigint fils, mirrors the server) ----
  const totals = useMemo(() => {
    let net = 0n;
    let tax = 0n;
    let selfAssessed = 0n;
    let recoverable = 0n;
    const byCode = new Map<string, { label: string; tax: bigint }>();
    const perRow = new Map<number, bigint>();
    for (const r of s.rows) {
      if (!used(r)) continue;
      const code = r.taxCodeId ? codeById.get(r.taxCodeId) : undefined;
      const a = purchaseLineAmounts(
        r.grid === 'item' ? r.quantity || '1' : '1',
        toMinor(r.rate, exp) ?? 0n,
        code,
        s.amountsAre,
      );
      perRow.set(r.key, s.amountsAre === 'inclusive' ? a.gross : a.net);
      net += a.net;
      tax += a.tax;
      selfAssessed += a.selfAssessed;
      const bp = code ? Math.round(Number(code.rate) * 100) : 0;
      const pct =
        !vatRegistered || (code && code.kind === 'standard' && code.returnBox === null)
          ? 0
          : canLimitRecovery(code)
            ? Number(r.recoverable || '100')
            : 100;
      recoverable += a.selfAssessed > 0n ? a.selfAssessed : recoverableTax(a.net, a.tax, bp, pct);
      if (code && a.tax > 0n) {
        const k = `${code.code} ${formatRate(code.rate)}`;
        byCode.set(k, {
          label: isIndia() ? code.name : `VAT ${formatRate(code.rate)} (${code.code})`,
          tax: (byCode.get(k)?.tax ?? 0n) + a.tax,
        });
      }
    }
    return {
      net,
      tax,
      total: net + tax,
      selfAssessed,
      blocked: tax + selfAssessed - recoverable,
      byCode: [...byCode.values()],
      perRow,
    };
  }, [s.rows, s.amountsAre, codeById, vatRegistered, exp]);
  const allocated =
    doc && kind !== 'expense'
      ? (toMinor(doc.total, exp) ?? 0n) - (toMinor(doc.balance, exp) ?? 0n)
      : 0n;
  const balanceDue = kind === 'expense' ? 0n : posted ? totals.total - allocated : totals.total;

  // ---- pickers ----
  const contactItems: ComboItem[] = useMemo(
    () =>
      (contacts.data ?? []).map((c) => ({
        id: c.id,
        label: c.displayName,
        hint: c.companyName ?? undefined,
      })),
    [contacts.data],
  );
  const accountItems: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => !['bank', 'credit_card'].includes(a.accountType))
        .map((a) => ({
          id: a.id,
          label: `${a.code} ${a.fullName}`,
          keywords: `${a.accountTypeName} ${a.detailTypeName}`,
          group: a.accountTypeName,
          hint: a.detailTypeName,
          indent: a.depth,
          disabled: a.isControl || a.detailType === 'undeposited_funds',
          disabledReason: 'Posted to by documents only',
        })),
    [accounts.data],
  );
  const itemItems: ComboItem[] = useMemo(
    () =>
      (items.data ?? [])
        .filter((i) => i.isPurchased)
        .map((i) => ({
          id: i.id,
          label: i.name,
          keywords: i.sku ?? '',
          hint: i.purchasePrice ? formatAmount(i.purchasePrice) : undefined,
        })),
    [items.data],
  );
  const paymentAccounts = (accounts.data ?? []).filter(
    (a) => !a.isControl && isPaymentAccount(a.detailType),
  );
  useEffect(() => {
    if (kind === 'expense' && !doc && !s.paymentAccountId && paymentAccounts.length)
      setS((p) => ({
        ...p,
        paymentAccountId: (
          paymentAccounts.find((a) => a.detailType === 'bank') ?? paymentAccounts[0]!
        ).id,
      }));
  }, [kind, doc, s.paymentAccountId, paymentAccounts]);
  const billsForNote = usePurchaseDocuments(
    'bill',
    kind === 'debit_note' ? (s.contactId ?? undefined) : undefined,
  );

  const chooseContact = (id: string | null) =>
    setS((p) => {
      const c = contacts.data?.find((x) => x.id === id);
      const terms = c ? c.paymentTermsDays : p.termsDays;
      return {
        ...p,
        contactId: id,
        termsDays: terms,
        fxRate: c && c.currency === (contact?.currency ?? currency) ? p.fxRate : '',
        dueDate:
          kind === 'bill' && !p.dueEdited && terms !== null
            ? addDays(p.issueDate, terms)
            : p.dueDate,
        relatedDocumentId: null,
        // the vendor's default category fills the first empty category line (QBO)
        rows: p.rows.map((r, i) =>
          c?.defaultAccountId &&
          r.grid === 'category' &&
          !r.accountId &&
          i === p.rows.findIndex((x) => x.grid === 'category' && !x.accountId)
            ? { ...r, accountId: c.defaultAccountId }
            : r,
        ),
      };
    });

  const updateRow = (key: number, patch: Partial<Row>) =>
    setS((p) => ({ ...p, rows: p.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const chooseAccount = (key: number, accountId: string | null) => {
    const a = accountId ? accountById.get(accountId) : undefined;
    updateRow(key, {
      accountId,
      ...(a?.defaultTaxCodeId ? { taxCodeId: a.defaultTaxCodeId } : {}),
      ...(a && BLOCKED_BY_DEFAULT.has(a.detailType) ? { recoverable: '0' } : {}),
    });
  };

  const chooseItem = (key: number, itemId: string | null) => {
    const it = items.data?.find((i) => i.id === itemId);
    updateRow(key, {
      itemId,
      ...(it
        ? {
            description: it.purchaseDescription ?? it.name,
            rate:
              it.purchasePrice && currency !== home && fxRate
                ? fromHomePrice(it.purchasePrice, fxRate, exponentOf(currency))
                : (it.purchasePrice ?? ''),
            quantity: '1',
            taxCodeId: it.purchaseTaxCodeId ?? defaultCode(contact),
            accountId: it.purchaseAccountId,
          }
        : {}),
    });
  };

  // ---- save ----
  type Action = 'draft' | 'approve-close' | 'approve-new' | 'save';
  const lineBody = (r: Row) => {
    const code = r.taxCodeId ? codeById.get(r.taxCodeId) : undefined;
    return {
      itemId: r.grid === 'item' ? r.itemId : null,
      accountId: r.accountId,
      description: r.description,
      quantity: r.grid === 'item' ? r.quantity || '1' : '1',
      unitPrice: r.rate || '0',
      taxCodeId: r.taxCodeId,
      ...(canLimitRecovery(code) && r.recoverable !== '' && Number(r.recoverable) !== 100
        ? { recoverablePercent: r.recoverable }
        : {}),
      ...(tracking.classes === 'per_line' ? { classId: r.classId } : {}),
      ...(tracking.projects ? { projectId: r.projectId } : {}),
      ...(kind === 'bill' && r.poLineId ? { purchaseOrderLineId: r.poLineId } : {}),
    };
  };
  const ordered = () => [
    ...s.rows.filter((r) => r.grid === 'category' && used(r)),
    ...s.rows.filter((r) => r.grid === 'item' && used(r)),
  ];
  const body = (approve: boolean) => ({
    contactId: s.contactId,
    issueDate: s.issueDate,
    ...(kind === 'bill' ? { termsDays: s.termsDays, dueDate: s.dueDate || null } : {}),
    supplyDate: s.supplyDate || null,
    amountsAre: s.amountsAre,
    reference: s.reference,
    memo: s.memo,
    ...(kind === 'debit_note'
      ? { creditReason: s.creditReason, relatedDocumentId: s.relatedDocumentId }
      : {}),
    ...(kind === 'expense'
      ? { paymentAccountId: s.paymentAccountId, paymentMethod: s.paymentMethod || null }
      : {}),
    ...(foreign && s.fxRate ? { fxRate: s.fxRate } : {}),
    ...trackingBody({ ...s.tracking, projectId: null }, { ...tracking, projects: false }),
    lines: ordered().map(lineBody),
    approve,
  });

  /** "Make recurring" (QBO): carry the form over to a new recurring template. */
  const makeRecurring = () => {
    if (kind === 'bill' || kind === 'expense') {
      setDocumentTemplateSeed({ kind, form: body(false) });
      leaving.current = true;
      void navigate({ to: '/accounting/recurring/new', search: { kind } });
    }
  };

  const save = useMutation({
    mutationFn: async (action: Action) => {
      const approve = action === 'approve-close' || action === 'approve-new';
      if (doc)
        return api<PurchaseDocument>(`/orgs/${orgId}/purchases/documents/${doc.id}`, {
          method: 'PUT',
          body: JSON.stringify({ ...body(approve), version: doc.version }),
        });
      if (inbox.data && inbox.data.status !== 'recorded')
        return (
          await api<{ item: InboxItem; document: PurchaseDocument }>(
            `/orgs/${orgId}/inbox/${inbox.data.id}/convert?version=${inbox.data.version}`,
            { method: 'POST', body: JSON.stringify({ kind, ...body(approve) }) },
          )
        ).document;
      return api<PurchaseDocument>(`/orgs/${orgId}/purchases/documents`, {
        method: 'POST',
        body: JSON.stringify({ kind, ...body(approve) }),
      });
    },
    onSuccess: async (saved, action) => {
      await qc.invalidateQueries();
      const what = `${info.label}${saved.number ? ` ${saved.number}` : ''}`;
      toast(
        clientDraft
          ? `${what} sent to your accountant for approval.`
          : isPO
            ? `${what} saved.`
            : saved.status === 'draft'
              ? `${what} saved as a draft.`
              : action === 'save' && posted
                ? `${what} updated and reposted.`
                : `${what} approved and posted.`,
        { link: { to: `${info.path}/${saved.id}`, label: 'View' } },
      );
      for (const w of saved.warnings) toast(w, { tone: 'error' });
      leaving.current = true;
      if (action === 'approve-new') {
        void navigate({ to: `${info.path}/new`, search: {}, replace: Boolean(doc) });
        if (!doc) {
          const fresh = initialState(null, null, null);
          initial.current = JSON.stringify(fresh);
          setS(fresh);
          leaving.current = false;
        }
      } else void navigate({ to: inbox.data ? '/documents/inbox' : info.listPath });
      setErrors([]);
      setFieldErrors({});
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        const details = Array.isArray(e.details)
          ? (e.details as Array<{ path: string; message: string }>)
          : [];
        // map server line indexes (categories first, then items) back to rows
        const order = ordered();
        setFieldErrors(
          Object.fromEntries(
            details.map((d) => {
              const m = /^lines\.(\d+)\.(.+)$/.exec(d.path);
              return m && order[Number(m[1])]
                ? [`row.${order[Number(m[1])]!.key}.${m[2]}`, d.message]
                : [d.path, d.message];
            }),
          ),
        );
        setErrors(details.length ? details.map((d) => capital(d.message)) : [capital(e.message)]);
      } else setErrors([String(e)]);
    },
  });

  const submit = (action: Action) => {
    if (save.isPending || readOnly) return;
    const problems: string[] = [];
    if (!s.contactId && kind !== 'expense') problems.push(`Choose a ${vendorWord.toLowerCase()}.`);
    if (!s.rows.some(used)) problems.push('Add at least one line.');
    if (kind === 'expense' && !s.paymentAccountId) problems.push('Choose the payment account.');
    ordered().forEach((r, i) => {
      if (!r.taxCodeId && s.amountsAre !== 'out_of_scope')
        problems.push(`Line ${i + 1}: choose a ${taxWord()} code.`);
    });
    if (problems.length) {
      setErrors(problems);
      return;
    }
    save.mutate(action);
  };

  const voidM = useMutation({
    mutationFn: ({ date, reason }: { date: string; reason: string }) =>
      api<PurchaseDocument>(`/orgs/${orgId}/purchases/documents/${doc!.id}/void`, {
        method: 'POST',
        body: JSON.stringify({ date, reason }),
      }),
    onSuccess: async (d) => {
      await qc.invalidateQueries();
      toast(`${info.label} ${d.number} voided.`);
      setVoiding(false);
      leaving.current = true;
      void navigate({ to: `${info.path}/${d.id}`, replace: true });
    },
  });
  const del = useMutation({
    mutationFn: () =>
      api<void>(`/orgs/${orgId}/purchases/documents/${doc!.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      leaving.current = true;
      qc.removeQueries({ queryKey: ['purchases', orgId, 'document', doc!.id] });
      await navigate({ to: info.listPath });
      toast(`${info.label} deleted.`);
      await qc.invalidateQueries({ queryKey: ['purchases', orgId], refetchType: 'active' });
    },
    onError: (e) => setErrors([e instanceof ApiError ? capital(e.message) : String(e)]),
  });
  /** Purchase orders: close every line, or open them again (document 06 E10). */
  const closePO = useMutation({
    mutationFn: (closed: boolean) =>
      api<PurchaseDocument>(
        `/orgs/${orgId}/purchases/documents/${doc!.id}/${closed ? 'close' : 'reopen'}`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
    onSuccess: async (d) => {
      await qc.invalidateQueries({ queryKey: ['purchases', orgId] });
      toast(`Purchase order ${d.number} ${d.status === 'closed' ? 'closed' : 'reopened'}.`);
      leaving.current = true;
      void navigate({ to: `${info.path}/${d.id}`, replace: true });
    },
    onError: (e) => setErrors([e instanceof ApiError ? capital(e.message) : String(e)]),
  });

  const canApply =
    kind === 'debit_note' &&
    isStaff &&
    (doc?.status === 'approved' || doc?.status === 'partially_paid');
  const canPay =
    kind === 'bill' && isStaff && (doc?.status === 'approved' || doc?.status === 'partially_paid');

  const close = () => void navigate({ to: info.listPath });
  const primary: Action = clientDraft ? 'draft' : posted ? 'save' : 'approve-close';
  useHotkeys({
    'ctrl+alt+s': () => submit(primary),
    'ctrl+alt+d': () => submit(clientDraft ? 'draft' : posted ? 'save' : 'approve-new'),
    'ctrl+alt+x': close,
  });

  const fe = (path: string) => fieldErrors[path];
  const rowErr = (r: Row, f: string) => Boolean(fieldErrors[`row.${r.key}.${f}`]);
  const title = doc?.number ? `${info.label} ${doc.number}` : info.label;
  const termsOptions =
    TERMS.some((t) => t.days === s.termsDays) || s.termsDays === null
      ? TERMS
      : [...TERMS, { days: s.termsDays, label: `Net ${s.termsDays}` }];

  const vatSelect = (r: Row, n: string) => (
    <select
      aria-label={`${taxWord()}, ${n}`}
      className={cn(
        'h-9 w-full rounded-md border border-input bg-card px-2 text-sm',
        rowErr(r, 'taxCodeId') && 'border-destructive',
      )}
      disabled={readOnly}
      value={r.taxCodeId ?? ''}
      onChange={(e) => {
        const code = codeById.get(e.target.value);
        updateRow(r.key, {
          taxCodeId: e.target.value || null,
          ...(canLimitRecovery(code) ? {} : { recoverable: '100' }),
        });
      }}
    >
      <option value="" />
      {purchaseCodes
        .concat(
          r.taxCodeId &&
            !purchaseCodes.some((t) => t.id === r.taxCodeId) &&
            codeById.get(r.taxCodeId)
            ? [codeById.get(r.taxCodeId)!]
            : [],
        )
        .map((t) => (
          <option key={t.id} value={t.id} title={t.description ?? t.name}>
            {t.code} {formatRate(t.rate)}
          </option>
        ))}
    </select>
  );
  const recoveryCell = (r: Row, n: string) => {
    const code = r.taxCodeId ? codeById.get(r.taxCodeId) : undefined;
    if (!canLimitRecovery(code) || !vatRegistered)
      return (
        <span
          className="block px-2 text-right text-xs text-muted-foreground"
          title={recoveryHint(code)}
        >
          {recoveryText(code, vatRegistered)}
        </span>
      );
    return (
      <Input
        aria-label={`Recoverable ${taxWord()} %, ${n}`}
        className={cn('tnum text-right', rowErr(r, 'recoverablePercent') && 'border-destructive')}
        inputMode="decimal"
        title={`Share of the ${taxWord()} you can recover: 100 in full, 0 when blocked (staff meals, personal use), or your recovery rate`}
        value={r.recoverable}
        disabled={readOnly}
        onChange={(e) => updateRow(r.key, { recoverable: e.target.value.replace(/[^\d.]/g, '') })}
      />
    );
  };
  const deleteButton = (r: Row, n: string) =>
    !readOnly ? (
      <Button
        variant="ghost"
        size="icon-sm"
        tabIndex={-1}
        aria-label={`Delete ${n}`}
        onClick={() =>
          setS((p) => {
            const next = p.rows.filter((x) => x.key !== r.key);
            return {
              ...p,
              rows: next.some((x) => x.grid === r.grid) ? next : [...next, emptyRow(r.grid)],
            };
          })
        }
      >
        <Trash2 className="size-4" />
      </Button>
    ) : null;
  const amountCell = (r: Row, n: string, grid: Grid) => {
    const amt = totals.perRow.get(r.key);
    return (
      <AmountInput
        aria-label={`Amount, ${n}`}
        exp={exp}
        value={
          grid === 'category'
            ? r.rate
            : amt !== undefined && amt !== 0n
              ? minorToPlain(amt, exp)
              : ''
        }
        disabled={readOnly}
        invalid={rowErr(r, 'unitPrice')}
        onValueChange={(v) => {
          if (grid === 'category') {
            updateRow(r.key, {
              rate: v,
              ...(v && !r.taxCodeId ? { taxCodeId: defaultCode(contact) } : {}),
            });
            return;
          }
          const target = toMinor(v, exp) ?? 0n;
          const rate = v ? rateFor(target, r.quantity || '1') : null;
          updateRow(r.key, {
            rate: rate === null ? '' : minorToPlain(rate, exp),
            ...(v && !r.taxCodeId ? { taxCodeId: defaultCode(contact) } : {}),
          });
        }}
      />
    );
  };
  const addRows = (grid: Grid) =>
    setS((p) => ({ ...p, rows: [...p.rows, emptyRow(grid), emptyRow(grid)] }));
  const clearRows = (grid: Grid) =>
    setS((p) => ({
      ...p,
      rows: [...p.rows.filter((r) => r.grid !== grid), emptyRow(grid), emptyRow(grid)],
    }));
  const categoryRows = s.rows.filter((r) => r.grid === 'category');
  const itemRows = s.rows.filter((r) => r.grid === 'item');

  return (
    <OverlayPage
      title={title}
      subtitle={
        doc ? <StatusBadge kind={kind} status={doc.status} overdue={doc.overdue} /> : undefined
      }
      onClose={close}
      footerLeft={
        <>
          <Button variant="outline" onClick={close}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly ? (
            <Button
              variant="ghost"
              onClick={() => {
                setS(JSON.parse(initial.current) as FormState);
                setErrors([]);
                setFieldErrors({});
              }}
            >
              {doc ? 'Revert' : 'Clear'}
            </Button>
          ) : null}
        </>
      }
      footerCentre={
        <>
          {canPay ? (
            <Button variant="outline" asChild>
              <Link to="/expenses/pay-bills" search={{ billId: doc!.id }}>
                Pay bill
              </Link>
            </Button>
          ) : null}
          {canApply ? (
            <Button variant="outline" onClick={() => setApplying(true)}>
              Apply to bills
            </Button>
          ) : null}
          {isPO && doc && isStaff ? (
            <>
              {doc.status === 'open' ? (
                <Button variant="outline" asChild>
                  <Link to="/expenses/bills/new" search={{ fromPurchaseOrder: doc.id }}>
                    Copy to bill
                  </Link>
                </Button>
              ) : null}
              {doc.status === 'open' || !poFullyReceived ? (
                <Button
                  variant="ghost"
                  disabled={closePO.isPending}
                  onClick={() => closePO.mutate(doc.status === 'open')}
                >
                  {doc.status === 'open' ? 'Close purchase order' : 'Reopen'}
                </Button>
              ) : (
                <span className="self-center text-sm text-muted-foreground">
                  Received in full on bills
                </span>
              )}
            </>
          ) : isPO ? null : (kind === 'bill' || kind === 'expense') && isStaff ? (
            <Button variant="ghost" onClick={makeRecurring}>
              Make recurring
            </Button>
          ) : (
            <span title={COMING_SOON}>
              <Button variant="ghost" disabled>
                Make recurring
              </Button>
            </span>
          )}
          {doc ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1">
                  More <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem asChild>
                  <Link to={`${info.path}/new`} search={{ copyOf: doc.id }}>
                    Copy
                  </Link>
                </DropdownMenuItem>
                {posted && isStaff ? (
                  <DropdownMenuItem onSelect={() => setVoiding(true)}>Void</DropdownMenuItem>
                ) : null}
                {(doc.status === 'draft' || (isPO && !poReceived)) && isStaff ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      if (
                        window.confirm(
                          `Delete this ${info.label.toLowerCase()}? This cannot be undone.`,
                        )
                      )
                        del.mutate();
                    }}
                  >
                    {isPO ? 'Delete' : 'Delete draft'}
                  </DropdownMenuItem>
                ) : null}
                {doc.journalEntryId ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/accounting/journal-entries/$entryId"
                        params={{ entryId: doc.journalEntryId }}
                      >
                        Transaction journal
                      </Link>
                    </DropdownMenuItem>
                  </>
                ) : null}
                {doc.voidEntryId ? (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: doc.voidEntryId }}
                    >
                      Void entry
                    </Link>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </>
      }
      footerRight={
        readOnly ? null : isPO ? (
          <Button disabled={save.isPending} onClick={() => submit('draft')} title="Ctrl+Alt+S">
            {save.isPending ? 'Saving…' : 'Save and close'}
          </Button>
        ) : clientDraft ? (
          <Button disabled={save.isPending} onClick={() => submit('draft')} title="Ctrl+Alt+S">
            {save.isPending ? 'Sending…' : 'Submit for approval'}
          </Button>
        ) : posted ? (
          <Button disabled={save.isPending} onClick={() => submit('save')} title="Ctrl+Alt+S">
            {save.isPending ? 'Saving…' : 'Save and close'}
          </Button>
        ) : (
          <>
            <Button variant="outline" disabled={save.isPending} onClick={() => submit('draft')}>
              Save draft
            </Button>
            <div className="inline-flex">
              <Button
                className="rounded-r-none"
                disabled={save.isPending}
                onClick={() => submit('approve-close')}
                title="Ctrl+Alt+S"
              >
                {save.isPending ? 'Saving…' : 'Approve and close'}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="rounded-l-none border-l border-primary-foreground/30 px-2"
                    aria-label="More save options"
                    disabled={save.isPending}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => submit('approve-new')}>
                    Approve and new
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => submit('draft')}>Save draft</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )
      }
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-5">
        {errors.length ? (
          <ErrorBanner>
            <ul className="list-disc pl-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </ErrorBanner>
        ) : null}
        {isVoid ? (
          <Notice tone="muted">
            This {info.label.toLowerCase()} is void{doc?.voidReason ? `: ${doc.voidReason}` : ''}.
            Its posting was reversed; the number stays in the sequence.
          </Notice>
        ) : doc?.opening ? (
          <Notice tone="muted">
            Brought over from QuickBooks at the conversion date with its open balance. Its income or
            expense and VAT are in the opening balances, so it has no lines and cannot be edited;
            record payments against it as usual.
          </Notice>
        ) : posted ? (
          <Notice>
            Approved{doc?.approvedByName ? ` by ${doc.approvedByName}` : ''}
            {doc?.approvedAt ? ` on ${formatDate(doc.approvedAt)}` : ''}. Saving changes reverses
            the original posting and posts the edited {info.label.toLowerCase()}.
            {allocated > 0n
              ? ` ${currency} ${formatMinor(allocated, exp)} is already paid or applied.`
              : ''}
          </Notice>
        ) : null}
        {isPO ? (
          <Notice tone="muted">
            A purchase order is not posted: it records what you ordered. Bills receive it with Copy
            to bill; it closes when every line is received
            {doc?.status === 'closed' ? '. Closed' : ''}.
            {poReceived ? ' Lines received on bills cannot change; close the order instead.' : ''}
          </Notice>
        ) : null}
        {!isStaff && !clientEdit && me.isSuccess ? (
          <Notice tone="muted">Only your accounting team can change expenses and bills.</Notice>
        ) : null}
        {clientDraft ? (
          <Notice>
            Your accountant reviews this {info.label.toLowerCase()} and approves it before it is
            posted. Upload the supplier&apos;s bill under Documents if you have it.
          </Notice>
        ) : null}
        {inbox.data && !doc ? <InboxSource item={inbox.data} /> : null}
        {kind === 'bill' && !readOnly && s.contactId ? (
          <OpenPurchaseOrders
            contactId={s.contactId}
            linked={new Set(s.rows.flatMap((r) => (r.poNumber ? [r.poNumber] : [])))}
            onAdd={(po) =>
              setS((p) => ({
                ...p,
                rows: [...p.rows.filter(used), ...openPurchaseOrderRows(po), emptyRow('category')],
              }))
            }
          />
        ) : null}
        {doc && doc.status === 'draft' ? (
          <ApprovalStrip documentId={doc.id} kind={kind} status={doc.status} />
        ) : null}

        {/* ---- header ---- */}
        <div className="flex flex-wrap items-start gap-6">
          <div className="grid flex-1 grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            <Field
              label={kind === 'expense' ? vendorWord : `${vendorWord} *`}
              error={fe('contactId')}
              className="col-span-2"
            >
              {() => (
                <Combobox
                  ariaLabel={vendorWord}
                  items={contactItems}
                  value={s.contactId}
                  invalid={Boolean(fe('contactId'))}
                  placeholder={
                    kind === 'expense'
                      ? 'Choose a payee (optional)'
                      : `Choose a ${vendorWord.toLowerCase()}`
                  }
                  onChange={readOnly ? () => undefined : chooseContact}
                  onAddNew={
                    readOnly || !isStaff
                      ? undefined
                      : (typed) =>
                          openNewDrawer({
                            kind: 'vendor',
                            name: typed,
                            onSaved: (c) => {
                              void qc
                                .invalidateQueries({ queryKey: ['contacts'] })
                                .then(() => chooseContact(c.id));
                            },
                          })
                  }
                />
              )}
            </Field>
            {kind === 'expense' ? (
              <>
                <Field label="Payment account *" error={fe('paymentAccountId')}>
                  {(id) => (
                    <NativeSelect
                      id={id}
                      disabled={readOnly}
                      value={s.paymentAccountId ?? ''}
                      aria-invalid={Boolean(fe('paymentAccountId')) || undefined}
                      onChange={(e) => set('paymentAccountId', e.target.value || null)}
                    >
                      <option value="">Choose an account</option>
                      {paymentAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} {a.fullName}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
                <Field label="Payment method">
                  {(id) => (
                    <NativeSelect
                      id={id}
                      disabled={readOnly}
                      value={s.paymentMethod}
                      onChange={(e) => set('paymentMethod', e.target.value)}
                    >
                      <option value="" />
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              </>
            ) : null}
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {kind === 'expense'
                ? 'Amount'
                : kind === 'debit_note'
                  ? 'Credit'
                  : isPO
                    ? 'Total'
                    : 'Balance due'}
            </div>
            <div className="tnum text-[32px] font-semibold leading-tight" data-testid="balance-due">
              {currency} {formatMinor(kind === 'expense' ? totals.total : balanceDue, exp)}
            </div>
            {foreign ? (
              <div className="tnum text-sm text-muted-foreground" data-testid="balance-due-home">
                {(() => {
                  const h = toHome(kind === 'expense' ? totals.total : balanceDue);
                  return h === null ? '' : `${home} ${formatMinor(h)}`;
                })()}
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-4">
          {kind !== 'expense' ? (
            <Field label="Mailing address" className="row-span-2">
              {() => (
                <div
                  className="min-h-20 whitespace-pre-line rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
                  data-testid="mailing-address"
                >
                  {contact
                    ? formatAddress(contact.billingAddress) || 'No address on the vendor.'
                    : doc?.billingAddress
                      ? Object.values(doc.billingAddress).filter(Boolean).join('\n')
                      : '—'}
                  {contact?.trn ? `\nTRN ${formatTrn(contact.trn)}` : ''}
                </div>
              )}
            </Field>
          ) : null}
          {kind === 'bill' ? (
            <Field label="Terms">
              {(id) => (
                <NativeSelect
                  id={id}
                  disabled={readOnly}
                  value={s.termsDays === null ? '' : String(s.termsDays)}
                  onChange={(e) => {
                    const t = e.target.value === '' ? null : Number(e.target.value);
                    setS((p) => ({
                      ...p,
                      termsDays: t,
                      dueEdited: false,
                      dueDate: t === null ? p.dueDate : addDays(p.issueDate, t),
                    }));
                  }}
                >
                  <option value="">Choose terms</option>
                  {termsOptions.map((t) => (
                    <option key={t.days} value={t.days}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          <Field label={`${info.dateLabel} *`} error={fe('issueDate')}>
            {(id) => (
              <DateInput
                id={id}
                value={s.issueDate}
                disabled={readOnly}
                onChange={(v) =>
                  setS((p) => ({
                    ...p,
                    issueDate: v,
                    dueDate:
                      kind === 'bill' && !p.dueEdited && p.termsDays !== null
                        ? addDays(v, p.termsDays)
                        : p.dueDate,
                  }))
                }
              />
            )}
          </Field>
          {kind === 'bill' ? (
            <Field label="Due date" error={fe('dueDate')}>
              {(id) => (
                <DateInput
                  id={id}
                  value={s.dueDate}
                  disabled={readOnly}
                  onChange={(v) => setS((p) => ({ ...p, dueDate: v, dueEdited: true }))}
                />
              )}
            </Field>
          ) : null}
          {foreign ? (
            <ExchangeRateField
              currency={currency}
              home={home}
              date={s.issueDate}
              value={s.fxRate}
              disabled={readOnly}
              error={fe('fxRate')}
              onChange={(v) => set('fxRate', v)}
            />
          ) : null}
          <Field
            label={kind === 'bill' ? 'Bill no.' : kind === 'expense' ? 'Ref no.' : 'Vendor ref no.'}
            hint={kind === 'bill' ? "The vendor's invoice number" : undefined}
          >
            {(id) => (
              <Input
                id={id}
                value={s.reference}
                maxLength={100}
                disabled={readOnly}
                onChange={(e) => set('reference', e.target.value)}
              />
            )}
          </Field>
          <Field label={kind === 'expense' ? 'Expense no.' : isPO ? 'PO no.' : 'Internal no.'}>
            {(id) => (
              <Input
                id={id}
                value={doc?.number ?? ''}
                placeholder={isPO ? 'Assigned when saved' : 'Assigned on approval'}
                disabled
              />
            )}
          </Field>
          {kind === 'bill' ? (
            <Field label="Date of supply" hint="If different from the bill date">
              {(id) => (
                <DateInput
                  id={id}
                  value={s.supplyDate}
                  disabled={readOnly}
                  onChange={(v) => set('supplyDate', v)}
                />
              )}
            </Field>
          ) : null}
          {kind === 'debit_note' ? (
            <>
              <Field label="Original bill" error={fe('relatedDocumentId')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={readOnly || !s.contactId}
                    value={s.relatedDocumentId ?? ''}
                    onChange={(e) => set('relatedDocumentId', e.target.value || null)}
                  >
                    <option value="">None</option>
                    {(billsForNote.data ?? [])
                      .filter((d) => POSTED.includes(d.status) || d.id === s.relatedDocumentId)
                      .map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.number}
                          {d.reference ? ` (${d.reference})` : ''} · {formatDate(d.issueDate)} ·{' '}
                          {d.currency} {formatAmount(d.total)}
                        </option>
                      ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label="Reason" className="col-span-2">
                {(id) => (
                  <Input
                    id={id}
                    value={s.creditReason}
                    maxLength={500}
                    disabled={readOnly}
                    placeholder="Returned goods, price adjustment…"
                    onChange={(e) => set('creditReason', e.target.value)}
                  />
                )}
              </Field>
            </>
          ) : null}
        </div>

        <TrackingHeaderFields
          value={s.tracking}
          onChange={(v) => set('tracking', v)}
          disabled={readOnly}
          withProject={false}
        />

        <div className="flex items-center gap-2 self-end text-sm">
          <label htmlFor="amounts-are" className="text-muted-foreground">
            Amounts are
          </label>
          <NativeSelect
            id="amounts-are"
            className="w-[200px]"
            disabled={readOnly}
            value={s.amountsAre}
            onChange={(e) => set('amountsAre', e.target.value as AmountsAre)}
          >
            <option value="exclusive">Exclusive of {taxWord()}</option>
            <option value="inclusive">Inclusive of {taxWord()}</option>
            <option value="out_of_scope">Out of scope of {taxWord()}</option>
          </NativeSelect>
        </div>

        {/* ---- Category details ---- */}
        <GridSection title="Category details">
          <table className="w-full min-w-[820px] table-fixed text-sm" aria-label="Category details">
            <thead className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-10 px-2 py-2">#</th>
                <th className="w-[26%] px-2 py-2">Category</th>
                <th className="px-2 py-2">Description</th>
                <th className="w-[140px] px-2 py-2 text-right">Amount</th>
                <th className="w-[120px] px-2 py-2">{taxWord()}</th>
                <th className="w-[100px] px-2 py-2 text-right">Recoverable</th>
                {trackingHeads}
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {categoryRows.map((r, i) => {
                const n = `category line ${i + 1}`;
                return (
                  <tr
                    key={r.key}
                    className="border-b last:border-0"
                    data-testid={`category-${i + 1}`}
                  >
                    <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                    <td className="px-1 py-1.5">
                      <Combobox
                        ariaLabel={`Category, ${n}`}
                        items={accountItems}
                        value={r.accountId}
                        invalid={rowErr(r, 'accountId')}
                        onChange={readOnly ? () => undefined : (id) => chooseAccount(r.key, id)}
                      />
                    </td>
                    <td className="px-1 py-1.5">
                      <Input
                        aria-label={`Description, ${n}`}
                        value={r.description}
                        disabled={readOnly}
                        maxLength={1000}
                        onChange={(e) => updateRow(r.key, { description: e.target.value })}
                      />
                    </td>
                    <td className="px-1 py-1.5">{amountCell(r, n, 'category')}</td>
                    <td className="px-1 py-1.5">{vatSelect(r, n)}</td>
                    <td className="px-1 py-1.5">{recoveryCell(r, n)}</td>
                    {trackingCells(r, n)}
                    <td className="px-1 py-1.5 text-center">{deleteButton(r, n)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </GridSection>
        {!readOnly ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => addRows('category')}>
              Add lines
            </Button>
            <Button variant="outline" size="sm" onClick={() => clearRows('category')}>
              Clear all lines
            </Button>
          </div>
        ) : null}

        {/* ---- Item details ---- */}
        {showItems ? (
          <>
            <GridSection title="Item details">
              <table
                className="w-full min-w-[1000px] table-fixed text-sm"
                aria-label="Item details"
              >
                <thead className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="w-10 px-2 py-2">#</th>
                    <th className="w-[20%] px-2 py-2">Product/service</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="w-[80px] px-2 py-2 text-right">Qty</th>
                    <th className="w-[120px] px-2 py-2 text-right">Rate</th>
                    <th className="w-[130px] px-2 py-2 text-right">Amount</th>
                    <th className="w-[120px] px-2 py-2">{taxWord()}</th>
                    <th className="w-[100px] px-2 py-2 text-right">Recoverable</th>
                    {trackingHeads}
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {itemRows.map((r, i) => {
                    const n = `item line ${i + 1}`;
                    return (
                      <tr
                        key={r.key}
                        className="border-b last:border-0"
                        data-testid={`item-${i + 1}`}
                      >
                        <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                        <td className="px-1 py-1.5">
                          <Combobox
                            ariaLabel={`Product/service, ${n}`}
                            items={itemItems}
                            value={r.itemId}
                            invalid={rowErr(r, 'itemId') || rowErr(r, 'accountId')}
                            onChange={readOnly ? () => undefined : (id) => chooseItem(r.key, id)}
                            onAddNew={
                              readOnly || !isStaff
                                ? undefined
                                : (typed) =>
                                    openNewDrawer({
                                      kind: 'item',
                                      name: typed,
                                      onSaved: (it) => {
                                        void qc
                                          .invalidateQueries({ queryKey: ['items'] })
                                          .then(() => chooseItem(r.key, it.id));
                                      },
                                    })
                            }
                          />
                        </td>
                        <td className="px-1 py-1.5">
                          <Input
                            aria-label={`Description, ${n}`}
                            value={r.description}
                            disabled={readOnly}
                            maxLength={1000}
                            onChange={(e) => updateRow(r.key, { description: e.target.value })}
                          />
                        </td>
                        <td className="px-1 py-1.5">
                          <Input
                            aria-label={`Qty, ${n}`}
                            className="tnum text-right"
                            inputMode="decimal"
                            value={r.quantity}
                            disabled={readOnly}
                            aria-invalid={rowErr(r, 'quantity') || undefined}
                            placeholder={r.itemId ? '1' : ''}
                            onChange={(e) =>
                              updateRow(r.key, { quantity: e.target.value.replace(/[^\d.]/g, '') })
                            }
                          />
                        </td>
                        <td className="px-1 py-1.5">
                          <AmountInput
                            aria-label={`Rate, ${n}`}
                            exp={exp}
                            value={r.rate}
                            disabled={readOnly}
                            invalid={rowErr(r, 'unitPrice')}
                            onValueChange={(v) =>
                              updateRow(r.key, {
                                rate: v,
                                ...(v && !r.taxCodeId ? { taxCodeId: defaultCode(contact) } : {}),
                              })
                            }
                          />
                        </td>
                        <td className="px-1 py-1.5">{amountCell(r, n, 'item')}</td>
                        <td className="px-1 py-1.5">{vatSelect(r, n)}</td>
                        <td className="px-1 py-1.5">{recoveryCell(r, n)}</td>
                        {trackingCells(r, n)}
                        <td className="px-1 py-1.5 text-center">{deleteButton(r, n)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </GridSection>
            {!readOnly ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => addRows('item')}>
                  Add lines
                </Button>
                <Button variant="outline" size="sm" onClick={() => clearRows('item')}>
                  Clear all lines
                </Button>
              </div>
            ) : null}
          </>
        ) : !readOnly ? (
          <div>
            <Button variant="link" className="px-0" onClick={() => setShowItems(true)}>
              Show item details
            </Button>
          </div>
        ) : null}

        {/* ---- memo + totals ---- */}
        <div className="flex flex-wrap items-start justify-between gap-8">
          <div className="flex min-w-[320px] flex-1 flex-col gap-4">
            <Field label="Memo" className="max-w-xl">
              {(id) => (
                <Textarea
                  id={id}
                  value={s.memo}
                  maxLength={2000}
                  disabled={readOnly}
                  onChange={(e) => set('memo', e.target.value)}
                />
              )}
            </Field>
            {doc ? (
              <div className="max-w-xl">
                <AttachmentsPanel
                  entityType="document"
                  entityId={doc.id}
                  canChange={isStaff || clientEdit}
                />
              </div>
            ) : null}
            {doc && doc.allocations.length ? (
              <div
                className="max-w-xl rounded-lg border bg-card p-3 text-sm"
                data-testid="allocations"
              >
                <div className="mb-2 font-medium">
                  {kind === 'debit_note' ? 'Applied to' : 'Payments and credits'}
                </div>
                <ul className="divide-y">
                  {doc.allocations.map((a) => (
                    <li key={a.id} className="flex justify-between py-1">
                      <Link
                        className="text-primary hover:underline"
                        to={
                          a.type === 'payment'
                            ? `/expenses/payments/${a.refId}`
                            : a.type === 'debit_note'
                              ? `/expenses/debit-notes/${a.refId}`
                              : `/expenses/bills/${a.refId}`
                        }
                      >
                        {a.type === 'payment'
                          ? 'Bill payment'
                          : a.type === 'debit_note'
                            ? 'Debit note'
                            : 'Bill'}{' '}
                        {a.refNumber}
                      </Link>
                      <span className="tnum">
                        {formatDate(a.date)} · {currency} {formatAmount(a.amount)}
                        {a.exchangeGainLoss ? (
                          <span className="block text-xs text-muted-foreground">
                            Exchange {a.exchangeGainLoss.startsWith('-') ? 'loss' : 'gain'} {home}{' '}
                            {formatAmount(a.exchangeGainLoss.replace('-', ''))}
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {doc?.relatedDocumentId ? (
              <p className="text-sm text-muted-foreground">
                Original bill{' '}
                <Link
                  className="text-primary hover:underline"
                  to={`/expenses/bills/${doc.relatedDocumentId}`}
                >
                  {doc.relatedDocumentNumber}
                </Link>
              </p>
            ) : null}
          </div>
          <dl
            className="grid w-[380px] grid-cols-[1fr_auto] gap-x-6 gap-y-2 text-sm"
            data-testid="totals"
          >
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="tnum text-right" data-testid="subtotal">
              {formatMinor(totals.net, exp)}
            </dd>
            {totals.byCode.map((b) => (
              <Row2 key={b.label} label={b.label} value={formatMinor(b.tax, exp)} />
            ))}
            {totals.byCode.length === 0 ? (
              <Row2 label={taxWord()} value={formatMinor(0n, exp)} />
            ) : null}
            <dt className="border-t pt-2 font-semibold">Total</dt>
            <dd className="tnum border-t pt-2 text-right font-semibold" data-testid="total">
              {currency} {formatMinor(totals.total, exp)}
            </dd>
            {kind !== 'expense' ? (
              <>
                {allocated > 0n ? (
                  <Row2
                    label={kind === 'debit_note' ? 'Applied' : 'Payments and credits'}
                    value={`-${formatMinor(allocated, exp)}`}
                  />
                ) : null}
                <dt className="font-semibold">
                  {kind === 'debit_note' ? 'Credit remaining' : 'Balance due'}
                </dt>
                <dd className="tnum text-right font-semibold">
                  {currency} {formatMinor(balanceDue, exp)}
                </dd>
              </>
            ) : null}
            {totals.selfAssessed > 0n ? (
              <Row2
                label={`Reverse charge or import ${taxWord()} you account for (not paid to the vendor)`}
                value={formatMinor(totals.selfAssessed, exp)}
              />
            ) : null}
            {totals.blocked > 0n ? (
              <Row2
                label={`${taxWord()} not recoverable (part of the cost)`}
                value={formatMinor(totals.blocked, exp)}
              />
            ) : null}
            <HomeTotals
              currency={currency}
              home={home}
              rate={fxRate}
              rows={[
                {
                  label: 'VAT',
                  minor:
                    totals.byCode.reduce((a, b) => a + (toHome(b.tax) ?? 0n), 0n) +
                    (totals.selfAssessed ? (toHome(totals.selfAssessed) ?? 0n) : 0n),
                  testId: 'home-vat',
                },
                { label: 'Total', minor: toHome(totals.total), testId: 'home-total' },
              ]}
            />
          </dl>
        </div>
        <p className="text-xs text-muted-foreground">
          Shortcuts: Ctrl+Alt+S {posted ? 'save' : 'approve and close'} · Ctrl+Alt+D approve and new
          · Ctrl+Alt+X close. Amounts accept arithmetic such as 1200*1.05.
        </p>
      </div>
      {doc && kind === 'debit_note' ? (
        <ApplyDebitNoteDialog
          note={doc}
          open={applying && canApply}
          onClose={() => setApplying(false)}
        />
      ) : null}
      {doc ? (
        <VoidDialog
          open={voiding}
          title={`Void ${info.label.toLowerCase()} ${doc.number ?? ''}`}
          description="Voiding posts a reversing entry, releases any payments or credits applied to it and keeps the number. This cannot be undone."
          minDate={doc.issueDate}
          pending={voidM.isPending}
          error={voidM.error ? capital(voidM.error.message) : null}
          onCancel={() => setVoiding(false)}
          onConfirm={(date, reason) => voidM.mutate({ date, reason })}
        />
      ) : null}
    </OverlayPage>
  );
}

function recoveryText(code: TaxCode | undefined, vatRegistered: boolean): string {
  if (!code || Number(code.rate) === 0) return '';
  if (!vatRegistered || (code.kind === 'standard' && code.returnBox === null)) return 'None';
  return isSelfAssessed(code) ? 'Full' : '100%';
}

function recoveryHint(code: TaxCode | undefined): string | undefined {
  if (!code) return undefined;
  if (isSelfAssessed(code))
    return `Reverse charge and import ${taxWord()} is accounted for as output tax and recovered in full`;
  if (code.kind === 'standard' && code.returnBox === null)
    return `${code.code}: the ${taxWord()} is part of the cost and is not reported on the return`;
  return undefined;
}

function GridSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border bg-card">
      <h2 className="border-b px-3 py-2 text-sm font-semibold">{title}</h2>
      {/* the line grid keeps its desktop column widths and scrolls sideways on a phone */}
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}

function Row2({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tnum text-right">{value}</dd>
    </>
  );
}

function Notice({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'muted' }) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        tone === 'info'
          ? 'border-info/30 bg-info/5 text-foreground'
          : 'border-border bg-muted text-muted-foreground',
      )}
      data-testid="form-notice"
    >
      {children}
    </div>
  );
}

const capital = (m: string) => (m ? m.charAt(0).toUpperCase() + m.slice(1) : m);

/**
 * QBO's "Add to bill" panel: the vendor's open purchase orders, each added as lines that receive
 * it (document 06 E11).
 */
function OpenPurchaseOrders({
  contactId,
  linked,
  onAdd,
}: {
  contactId: string;
  linked: Set<string>;
  onAdd: (po: PurchaseDocument) => void;
}) {
  const orgId = useOrgId()!;
  const orders = usePurchaseDocuments('purchase_order', contactId);
  const open = (orders.data ?? []).filter(
    (o) => o.status === 'open' && !linked.has(o.number ?? ''),
  );
  if (!open.length) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm"
      data-testid="open-purchase-orders"
    >
      <span className="text-muted-foreground">Open purchase orders for this vendor:</span>
      {open.map((o) => (
        <Button
          key={o.id}
          size="sm"
          variant="outline"
          onClick={() =>
            void api<PurchaseDocument>(`/orgs/${orgId}/purchases/documents/${o.id}`).then(onAdd)
          }
        >
          Add {o.number} ({formatAmount(o.total)})
        </Button>
      ))}
    </div>
  );
}
